import React from "react";
import {
  Box,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Skeleton,
  SxProps,
  Theme,
} from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import {
  StyledCard,
  IconBox,
  FlexRow,
  CardTitle,
  SubText,
  HeaderSkeleton,
  TextSkeleton,
  ChartSkeleton,
} from "./common";

/**
 * Chart type option for dropdown selector
 */
export interface ChartTypeOption {
  /** Value for the chart type */
  value: string;
  /** Display label in dropdown */
  label: string;
}

/**
 * ChartCard Component Props
 */
interface ChartCardProps {
  /** Chart title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Icon component for the header */
  icon: React.ReactElement<SvgIconComponent>;
  /** Icon gradient theme */
  iconGradient?: string;
  /** Chart content (ResponsiveContainer with Recharts components) */
  children: React.ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Chart height in pixels */
  chartHeight?: number;
  /** Current chart type value (for dropdown) */
  chartType?: string;
  /** Chart type options for dropdown */
  chartTypeOptions?: ChartTypeOption[];
  /** Chart type change handler */
  onChartTypeChange?: (value: string) => void;
  /** Custom skeleton loader (if not using default ChartSkeleton) */
  customSkeleton?: React.ReactNode;
  /** Custom card sx props */
  cardSx?: SxProps<Theme>;
  /** Custom chart container sx props */
  chartContainerSx?: SxProps<Theme>;
  /** Whether to show hover effect on card */
  hoverable?: boolean;
  /** Card elevation */
  elevation?: number;
}

/**
 * ChartCard Component
 *
 * Reusable chart card component with consistent styling and loading states.
 * Supports optional dropdown selector for switching chart types.
 *
 * Features:
 * - Consistent header with icon, title, and subtitle
 * - Optional chart type dropdown selector
 * - Skeleton loading state
 * - Responsive design
 * - Hover effects
 * - Gradient styling
 *
 * @example
 * ```tsx
 * // With chart type selector
 * <ChartCard
 *   title="Traffic Trends"
 *   subtitle="Entry/exit patterns and occupancy"
 *   icon={<ShowChart />}
 *   isLoading={isLoading}
 *   chartHeight={450}
 *   chartType={chartType}
 *   chartTypeOptions={[
 *     { value: "line", label: "Line Chart" },
 *     { value: "area", label: "Area Chart" }
 *   ]}
 *   onChartTypeChange={setChartType}
 * >
 *   <ResponsiveContainer>
 *     <LineChart data={data}>...</LineChart>
 *   </ResponsiveContainer>
 * </ChartCard>
 *
 * // Simple chart without selector
 * <ChartCard
 *   title="Vehicle Breakdown"
 *   subtitle="Total composition"
 *   icon={<Assessment />}
 *   isLoading={isLoading}
 *   chartHeight={300}
 * >
 *   <PieChart>...</PieChart>
 * </ChartCard>
 * ```
 */
const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  icon,
  iconGradient = "royalBlue",
  children,
  isLoading = false,
  chartHeight = 400,
  chartType,
  chartTypeOptions,
  onChartTypeChange,
  customSkeleton,
  cardSx,
  chartContainerSx,
  hoverable = true,
  elevation = 2,
}) => {
  const hasDropdown = chartType && chartTypeOptions && onChartTypeChange;

  const handleChartTypeChange = (event: SelectChangeEvent<string>) => {
    if (onChartTypeChange) {
      onChartTypeChange(event.target.value);
    }
  };

  // Default skeleton for charts with dropdown
  const defaultSkeleton = hasDropdown ? (
    <ChartSkeleton height={chartHeight} showHeader showDropdown />
  ) : (
    <>
      <HeaderSkeleton iconSize={36} titleWidth={150} />
      {subtitle && (
        <TextSkeleton width="80%" fontSize="0.875rem" sx={{ mb: 3 }} />
      )}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Skeleton
          variant="circular"
          width={chartHeight * 0.7}
          height={chartHeight * 0.7}
        />
      </Box>
    </>
  );

  return (
    <StyledCard
      elevation={elevation}
      borderRadius={3}
      showBorder
      borderColor="rgba(0,0,0,0.08)"
      hoverable={hoverable}
      sx={{
        height: "100%",
        overflow: "hidden",
        "&:hover": hoverable
          ? {
              borderColor: "rgba(102,126,234,0.2)",
            }
          : undefined,
        ...cardSx,
      }}
    >
      <CardContent sx={{ p: hasDropdown ? 0 : 3 }}>
        {isLoading ? (
          customSkeleton || defaultSkeleton
        ) : (
          <>
            {/* Header Section */}
            {hasDropdown ? (
              // Header with dropdown (for charts with type selector)
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                  p: 3,
                  pb: 2,
                  borderBottom: "2px solid",
                  borderColor: "divider",
                  background:
                    "linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.08) 100%)",
                }}
              >
                <FlexRow gap={1}>
                  <IconBox
                    icon={icon}
                    gradient={iconGradient}
                    size={36}
                    iconSize={20}
                    borderRadius={1.5}
                  />
                  <Box>
                    <CardTitle>{title}</CardTitle>
                    {subtitle && (
                      <Typography variant="caption" color="text.secondary">
                        {subtitle}
                      </Typography>
                    )}
                  </Box>
                </FlexRow>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 140,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1a237e",
                      },
                    },
                  }}
                >
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={chartType}
                    label="Chart Type"
                    onChange={handleChartTypeChange}
                  >
                    {chartTypeOptions?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              // Simple header (for static charts)
              <>
                <FlexRow gap={1} mb={2}>
                  <IconBox
                    icon={icon}
                    gradient={iconGradient}
                    size={36}
                    iconSize={20}
                    borderRadius={1.5}
                  />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ letterSpacing: "-0.3px" }}
                  >
                    {title}
                  </Typography>
                </FlexRow>
                {subtitle && (
                  <SubText sx={{ mb: 3, fontWeight: 500 }}>{subtitle}</SubText>
                )}
              </>
            )}

            {/* Chart Container */}
            <Box
              sx={{
                width: "100%",
                height: chartHeight,
                p: hasDropdown ? 3 : 0,
                pt: hasDropdown ? 2 : 0,
                background: hasDropdown
                  ? "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(102,126,234,0.02) 100%)"
                  : undefined,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ...chartContainerSx,
              }}
            >
              {children}
            </Box>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default ChartCard;
